import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { UserRole } from '../types';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDashboardTitle, shouldShowBusinessDashboard } from '../utils/navigationUtils';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import { X, Mail, ArrowLeft } from 'lucide-react';

interface InvitePersonnelFormData {
  email: string;
  roleIds: string[];
  canPerformServices: boolean | null;
}

const InvitePersonnelPage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [formData, setFormData] = useState<InvitePersonnelFormData>({
    email: '',
    roleIds: [],
    canPerformServices: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (businessId) {
      loadRoles();
    }
  }, [businessId]);

  const loadRoles = async () => {
    if (!businessId) {
      setError('Business information not loaded. Please try again.');
      return;
    }
    
    setRolesLoading(true);
    try {
      const rolesData = await apiService.getBusinessRoles(businessId);
      // Transform business roles to match UserRole interface for display
      const transformedRoles = rolesData
        .filter(role => role.is_active)
        .map((role: any) => ({
          id: role.id,
          userId: '', // Not applicable for business roles
          businessId: role.business_id,
          role: role.name,
          roleDescription: role.description,
          permissions: role.permissions?.map((p: any) => p.name) || [],
          isActive: role.is_active,
          createdAt: role.created_at,
          updatedAt: role.updated_at
        }));
      setRoles(transformedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load business roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds // If clicking on the currently selected role, don't allow deselection
        : [roleId] // Replace the current selection with the new role (ensuring exactly 1 role)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessId) {
      toast.error('Business information not available');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (formData.roleIds.length !== 1) {
      toast.error('Please select exactly one role');
      return;
    }

    if (formData.canPerformServices === null) {
      toast.error('Please select a service role');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.createInvitation(businessId, {
        email: formData.email.trim().toLowerCase(),
        roleIds: formData.roleIds,
        canPerformServices: formData.canPerformServices
      });
      
      toast.success('Invitation sent successfully!');
      navigate(`/dashboard/business?section=professionals`);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/dashboard/business?section=professionals`);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Navigation items are now handled by BusinessDashboardSidebar component

  if (rolesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="professionals"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
          />
        )}
        
        {/* Main Content */}
        <div className="flex flex-col flex-1">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Invite Personnel</h1>
              <div className="w-10"></div> {/* Spacer for centering */}
            </div>
          </div>
          
          <div className="flex-1 p-4 md:p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/business?section=professionals')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Personnel</span>
              </button>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Invite Personnel</h2>
              <p className="text-gray-600 mt-1">Send an invitation to join your business team</p>
            </div>

            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Email Input */}
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value.toLowerCase() }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-2">Assign Platform Role</h2>
                    <p className="text-sm text-gray-600">
                      Select exactly one role to assign to this personnel. Click on a role card to select it.
                    </p>
                  </div>

                  {roles.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500">No business roles available</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {roles.map((role) => (
                        <div
                          key={role.id}
                          onClick={() => handleRoleToggle(role.id)}
                          className={`relative bg-white rounded-lg shadow-sm border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                            formData.roleIds.includes(role.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {/* Check icon in top right corner */}
                          {formData.roleIds.includes(role.id) && (
                            <div className="absolute top-3 right-3">
                              <svg className="h-6 w-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          <div className="p-4">
                            <div className="mb-2">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 line-clamp-1 mb-2">
                                  {role.role}
                                </h3>
                                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                                  role.permissions?.some(p => ['manage_settings', 'manage_roles', 'manage_professionals'].includes(p)) ? 'bg-yellow-100 text-yellow-800' :
                                  role.permissions?.some(p => ['manage_availability', 'view_schedule'].includes(p)) ? 'bg-blue-100 text-blue-800' :
                                  role.permissions?.some(p => ['manage_appointments', 'manage_professionals'].includes(p)) ? 'bg-purple-100 text-purple-800' :
                                  role.permissions?.some(p => ['view_appointments', 'view_services'].includes(p)) ? 'bg-blue-100 text-blue-800' :
                                  (!role.permissions || role.permissions.length === 0) ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {role.permissions?.some(p => ['manage_settings', 'manage_roles', 'manage_professionals'].includes(p)) ? 'Full Access' :
                                   role.permissions?.some(p => ['manage_availability', 'view_schedule'].includes(p)) ? 'Limited Access' :
                                   role.permissions?.some(p => ['manage_appointments', 'manage_professionals'].includes(p)) ? 'Management Access' :
                                   role.permissions?.some(p => ['view_appointments', 'view_services'].includes(p)) ? 'Basic Access' :
                                   (!role.permissions || role.permissions.length === 0) ? 'Read Only' : 'User Role'}
                                </span>
                              </div>
                            </div>
                            
                            {role.role === 'Business Owner' ? (
                              <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                                <li>Manage services, staff, and availability</li>
                                <li>View & export all reports</li>
                                <li>Billing & integrations</li>
                              </ul>
                            ) : (
                              <p className="text-gray-600 text-sm mt-2">
                                {role.roleDescription || 'Role description not available'}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Service Role Selection (moved below Assign Platform Role) */}
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="mb-2">
                    <h2 className="text-lg font-medium text-gray-900 mb-2">Service Role</h2>
                    <p className="text-sm text-gray-600">
                      Specify whether this person can be booked by customers to perform services.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={formData.canPerformServices === true}
                      onClick={() => setFormData(prev => ({ ...prev, canPerformServices: true }))}
                      className={`text-left w-full rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formData.canPerformServices === true ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="font-semibold text-gray-900">Service Provider</div>
                        {formData.canPerformServices === true && (
                          <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Can perform services for customers. Appears in booking flow and can be assigned to appointments.
                      </div>
                    </button>

                    <button
                      type="button"
                      role="radio"
                      aria-checked={formData.canPerformServices === false}
                      onClick={() => setFormData(prev => ({ ...prev, canPerformServices: false }))}
                      className={`text-left w-full rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formData.canPerformServices === false ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="font-semibold text-gray-900">Non-service Staff</div>
                        {formData.canPerformServices === false && (
                          <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Cannot perform services for customers. Suitable for front desk, coordinators, or managers.
                      </div>
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || formData.roleIds.length !== 1 || formData.canPerformServices === null}
                    className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{isSubmitting ? 'Sending...' : 'Send Invitation'}</span>
                  </button>
                </div>
              </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitePersonnelPage; 